import { AssertEqualType } from '@lessonup/domain-utils';
import {
  AnswerByCategoryStudentPinSchema,
  DragAndDropStudentPinSchema,
  FillInTheBlanksStudentPinSchema,
  OpenQuestionStudentPinSchema,
  PinType,
  PollStudentPinSchema,
  QuizStudentPinSchema,
  SlideStudentPinSchema,
  SortingQuestionStudentPinSchema,
  TeacherPin,
  UnsupportedStudentPinSchema,
  WordCloudStudentPinSchema,
} from '@lessonup/pins-shared';
import { isNumber } from 'lodash';
import { z } from 'zod';

// TODO: commented for now
// export type TypeOfContent<P> = P extends AssignmentPin<infer C> ? C : never;

const DbIdentifiableSchema = z.object({ _id: z.string().min(1) });

const DbIdentifiableQuizStudentPinSchema = QuizStudentPinSchema.extend(DbIdentifiableSchema.shape);
const DbIdentifiableSlideStudentPinSchema = SlideStudentPinSchema.extend(DbIdentifiableSchema.shape);
const DbIdentifiableAnswerByCategoryStudentPinSchema = AnswerByCategoryStudentPinSchema.extend(
  DbIdentifiableSchema.shape
);
const DbIdentifiableWordCloudStudentPinSchema = WordCloudStudentPinSchema.extend(DbIdentifiableSchema.shape);
const DbIdentifiableOpenQuestionStudentPinSchema = OpenQuestionStudentPinSchema.extend(DbIdentifiableSchema.shape);
const DbIdentifiablePollStudentPinSchema = PollStudentPinSchema.extend(DbIdentifiableSchema.shape);
const DbIdentifiableFillInTheBlanksStudentPinSchema = FillInTheBlanksStudentPinSchema.extend(
  DbIdentifiableSchema.shape
);
const DbIdentifiableSortingQuestionStudentPinSchema = SortingQuestionStudentPinSchema.extend(
  DbIdentifiableSchema.shape
);
const DbIdentifiableDragAndDropStudentPinSchema = DragAndDropStudentPinSchema.extend(DbIdentifiableSchema.shape);
const DbIdentifiableUnsupportedStudentPinSchema = UnsupportedStudentPinSchema.extend(DbIdentifiableSchema.shape);

// TODO: Decide whether to use id or _id in firestore documents, adding _id for now to make porting less painful
// export const AssignmentPinSchema = StudentPinSchema;
export const AssignmentPinSchema = z.discriminatedUnion('type', [
  // QuizStudentPinSchema.omit({ id: true }).extend(DbIdentifiableSchema.shape),
  // SlideStudentPinSchema.omit({ id: true }).extend(DbIdentifiableSchema.shape),
  // DragQuestionStudentPinSchema.omit({ id: true }).extend(DbIdentifiableSchema.shape),
  // WordCloudStudentPinSchema.omit({ id: true }).extend(DbIdentifiableSchema.shape),
  // VideoStudentPinSchema.omit({ id: true }).extend(DbIdentifiableSchema.shape),
  // OpenQuestionStudentPinSchema.omit({ id: true }).extend(DbIdentifiableSchema.shape),
  // LinkStudentPinSchema.omit({ id: true }).extend(DbIdentifiableSchema.shape),
  // MapStudentPinSchema.omit({ id: true }).extend(DbIdentifiableSchema.shape),
  // PollStudentPinSchema.omit({ id: true }).extend(DbIdentifiableSchema.shape),
  DbIdentifiableQuizStudentPinSchema,
  DbIdentifiableSlideStudentPinSchema,
  DbIdentifiableAnswerByCategoryStudentPinSchema,
  DbIdentifiableWordCloudStudentPinSchema,
  DbIdentifiableOpenQuestionStudentPinSchema,
  DbIdentifiablePollStudentPinSchema,
  DbIdentifiableFillInTheBlanksStudentPinSchema,
  DbIdentifiableSortingQuestionStudentPinSchema,
  DbIdentifiableDragAndDropStudentPinSchema,
  DbIdentifiableUnsupportedStudentPinSchema,
]);

export type AssignmentPin = z.infer<typeof AssignmentPinSchema>;

// If this gives you an error, update the AssignmentPinSchema union
const ensureTypesAreInSync: AssertEqualType<PinType, AssignmentPin['type']> = true;

export type SlideAssignmentPin = Extract<AssignmentPin, { type: 'SLIDE' }>;
export type QuizAssignmentPin = Extract<AssignmentPin, { type: 'MULTIPLE_CHOICE' }>;
export type AnswerByCategoryAssignmentPin = Extract<AssignmentPin, { type: 'ANSWER_BY_CATEGORY' }>;
export type WordCloudAssignmentPin = Extract<AssignmentPin, { type: 'WORD_CLOUD' }>;
export type OpenQuestionAssignmentPin = Extract<AssignmentPin, { type: 'OPEN_QUESTION' }>;
export type PollAssignmentPin = Extract<AssignmentPin, { type: 'POLL' }>;
export type FillInTheBlanksAssignmentPin = Extract<AssignmentPin, { type: 'FILL_IN_THE_BLANKS' }>;
export type SortingQuestionAssignmentPin = Extract<AssignmentPin, { type: 'SORTING_QUESTION' }>;
export type DragAndDropAssignmentPin = Extract<AssignmentPin, { type: 'DRAG_AND_DROP' }>;

export type AnyAssignmentPin = z.infer<typeof AssignmentPinSchema>;

export namespace AssignmentPin {
  export type UpdateData = Partial<Pick<AssignmentPin['settings'], 'testPoints'> & Pick<AssignmentPin, 'order'>>;

  export function isSlide(pin: AssignmentPin): pin is SlideAssignmentPin {
    return pin.type === 'SLIDE';
  }

  export function isQuiz(pin: AssignmentPin): pin is QuizAssignmentPin {
    return pin.type === 'MULTIPLE_CHOICE';
  }

  export function isAnswerByCategory(pin: AssignmentPin): pin is AnswerByCategoryAssignmentPin {
    return pin.type === 'ANSWER_BY_CATEGORY';
  }

  export function isOpenQuestion(pin: AssignmentPin): pin is OpenQuestionAssignmentPin {
    return pin.type === 'OPEN_QUESTION';
  }

  export function isWordCloud(pin: AssignmentPin): pin is WordCloudAssignmentPin {
    return pin.type === 'WORD_CLOUD';
  }

  export function isPoll(pin: AssignmentPin): pin is PollAssignmentPin {
    return pin.type === 'POLL';
  }

  export function isSortingQuestion(pin: AssignmentPin): pin is SortingQuestionAssignmentPin {
    return pin.type === 'SORTING_QUESTION';
  }

  export function isFillInTheBlanks(pin: AssignmentPin): pin is FillInTheBlanksAssignmentPin {
    return pin.type === 'FILL_IN_THE_BLANKS';
  }

  export function isDragAndDrop(pin: AssignmentPin): pin is DragAndDropAssignmentPin {
    return pin.type === 'DRAG_AND_DROP';
  }

  export function assignmentPinFromTeacherPin(teacherPin: TeacherPin): AssignmentPin {
    let studentSettings = {};

    switch (teacherPin.type) {
      case 'MULTIPLE_CHOICE':
        studentSettings = {
          hasCorrectAnswer: teacherPin.teacherSettings.correctAnswers.length > 0,
        };
        break;
      case 'OPEN_QUESTION':
        studentSettings = {
          hasCorrectAnswer:
            teacherPin.teacherSettings.correctAnswers != null && teacherPin.teacherSettings.correctAnswers.length > 0,
          maximumNumberOfUploads: 0, // TODO: determine how many maximum uploads we allow
        };
        break;
      case 'SORTING_QUESTION':
        studentSettings = {
          hasCorrectAnswer: false,
        };
        break;
      case 'FILL_IN_THE_BLANKS':
        studentSettings = {
          hasCorrectAnswer: teacherPin.teacherSettings.correctAnswers.length > 0,
        };
        break;
      default:
        studentSettings = {};
        break;
    }

    return AssignmentPinSchema.parse({
      ...teacherPin,
      _id: teacherPin.id, // TODO: @davey remove this if we decide to go for id
      studentSettings,
    });
  }

  export function timerForPin(pin: AssignmentPin): number | undefined {
    const timer = pin.settings.timer;
    if (isNumber(timer)) {
      return timer;
    }
    return undefined;
  }

  export function hasTestPoints(pin: AssignmentPin): boolean {
    return !!pin.settings.testPoints;
  }
}
